import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { canonicalHash } from '@/api/mock/sign';
import { signHashWithFounderKey } from '@/api/real/founderSign';

// ── Types ────────────────────────────────────────────────────────────────────

interface TaxCalcResponse {
  calc_id: string;
  tax_type: string;
  period: string;
  amount_agorot: number;
  calculation: Record<string, unknown>;
  status: string;
  founder_approved_at: string | null;
}

interface ActionResponse { id: string; action: string; }

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

async function approveWithFounderSig(path: string, body: { note?: string | null }) {
  const sig = await signHashWithFounderKey(canonicalHash(body));
  return apiFetch<ActionResponse>(path, {
    method: 'POST', body, headers: { 'X-Founder-Signature': sig },
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, highlight }: {
  label: string; value: string | number; sub?: string; color?: string; highlight?: boolean;
}) {
  return (
    <div style={{
      background: highlight ? 'rgba(125,211,252,0.08)' : 'var(--omega-surface, rgba(255,255,255,0.04))',
      border: highlight ? '1px solid rgba(125,211,252,0.4)' : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
      borderRadius: 6, padding: '14px 18px', minWidth: 140,
    }}>
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>{value}</div>
      {sub && <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceTaxVatPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const qc = useQueryClient();
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [calcOpen, setCalcOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveResult, setApproveResult] = useState<string | null>(null);

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'tax', 'vat', period],
    queryFn: () => apiFetch<TaxCalcResponse>(`/finance/tax/vat?period=${period}`),
    refetchInterval: 30_000,
  });

  async function handleApprove() {
    if (!data) return;
    setApproving(true);
    setApproveResult(null);
    try {
      const result = await approveWithFounderSig(
        `/finance/tax/submission/${data.calc_id}/approve`,
        { note: null },
      );
      setApproveResult(`Approved: ${result.id}`);
      await qc.invalidateQueries({ queryKey: ['finance', 'tax', 'vat', period] });
    } catch (e) {
      setApproveResult(`Error: ${(e as Error).message}`);
    } finally {
      setApproving(false);
    }
  }

  const statusColor = (s: string) => {
    switch (s) {
      case 'pending': return '#f59e0b';
      case 'approved': return '#22c55e';
      case 'filed': return '#7dd3fc';
      default: return 'var(--omega-ink-subtle)';
    }
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Taxes', to: '/omega/finance/tax' },
        { label: 'VAT' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          VAT — VAT
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>Period:</label>
          <input
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            style={inputStyle}
          />
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {!isPending && !isError && !data && (
        <div style={{ color: 'var(--omega-ink-muted)', fontSize: '9pt', fontFamily: 'var(--omega-mono)' }}>
          No VAT calculation for this period
        </div>
      )}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
            <StatCard label="VAT amount" value={fmtIls(data.amount_agorot)} highlight />
            <StatCard label="Status" value={data.status} color={statusColor(data.status)} />
            <StatCard
              label="Approved by Founder"
              value={data.founder_approved_at ? fmtDate(data.founder_approved_at) : '—'}
            />
          </div>

          <div style={{ marginBottom: 16, fontFamily: 'var(--omega-mono)', fontSize: '8.5pt' }}>
            <span style={{ color: 'var(--omega-ink-subtle)' }}>calc_id: </span>
            <span style={{ color: 'var(--omega-ink)' }}>{data.calc_id}</span>
            <span style={{ color: 'var(--omega-ink-subtle)', marginLeft: 16 }}>Period: </span>
            <span style={{ color: 'var(--omega-ink)' }}>{data.period}</span>
          </div>

          {/* Calculation collapsible */}
          <div style={{ marginBottom: 20 }}>
            <button
              onClick={() => setCalcOpen(o => !o)}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
                borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
                fontSize: '8.5pt', padding: '4px 12px', cursor: 'pointer',
              }}
            >
              {calcOpen ? '▾ Hide calculation' : '▸ Show calculation'}
            </button>
            {calcOpen && (
              <pre style={{
                marginTop: 8,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                borderRadius: 4, padding: '10px 14px',
                fontFamily: 'var(--omega-mono)', fontSize: '8pt',
                color: 'var(--omega-ink-muted)', overflowX: 'auto',
              }}>
                {JSON.stringify(data.calculation, null, 2)}
              </pre>
            )}
          </div>

          {/* Approve button */}
          {data.status !== 'approved' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={handleApprove}
                disabled={approving}
                style={{
                  background: 'rgba(34,197,94,0.12)',
                  border: '1px solid rgba(34,197,94,0.35)',
                  borderRadius: 4, color: '#22c55e', fontFamily: 'var(--omega-mono)',
                  fontSize: '8.5pt', fontWeight: 700, padding: '6px 16px',
                  cursor: approving ? 'not-allowed' : 'pointer',
                }}
              >
                {approving ? 'Approving…' : 'Approve ✓'}
              </button>
              {approveResult && (
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink-muted)' }}>
                  {approveResult}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
